import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { FunnelIcon } from "@heroicons/react/24/outline";
import axios from "../api/axiosConfig";

// Components
import RecipeGrid from "../components/RecipeGrid";
import RecipeGridLoader from "../components/loaders/DiscoverLoader";
import FilterDrawer from "../components/FilterDrawer";
import SearchBar from "../components/SearchBarLong";
import { useSearch } from "../util/SearchContext";

export default function DiscoverPage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true); // primary loading flag

  // URL params
  const [searchParams, setSearchParams] = useSearchParams();

  // Drawer open state
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Stable dependency for URL params.
  const paramsString = useMemo(() => searchParams.toString(), [searchParams]);

  // Memoize query and filter arrays based on the URL.
  const { searchQuery, setSearchQuery } = useSearch();
  const searchParamValue = searchParams.get("search") || "";
  const filters = useMemo(() => ({
    mealTypes: searchParams.get("mealType")?.split(",").filter(Boolean) || [],
    diets: searchParams.get("diet")?.split(",").filter(Boolean) || [],
    cuisines: searchParams.get("cuisine")?.split(",").filter(Boolean) || [],
    cookingMethods: searchParams.get("cookingMethod")?.split(",").filter(Boolean) || [],
  }), [paramsString]);

  // On mount, sync the URL's search query into context so the SearchBar displays it.
  useEffect(() => {
    const urlQuery = searchParams.get("search") || "";
    setSearchQuery(urlQuery);
  }, []);

  // Unified search call.
  const fetchUnifiedSearch = async (query) => {
    try {
      setLoading(true);
      const response = await axios.post("/api/search", {
        query,
        mealTypes: filters.mealTypes,
        diets: filters.diets,
        cuisines: filters.cuisines,
        cookingMethods: filters.cookingMethods,
      });
      setRecipes(response.data);
    } catch (err) {
      console.error("Error searching recipes:", err);
    } finally {
      setLoading(false);
    }
  };

  // Count active filters (for the Filter button badge)
  const selectedCount = useMemo(() => {
    return ["mealTypes", "diets", "cuisines", "cookingMethods"].reduce(
      (total, key) => total + filters[key].length,
      0
    );
  }, [filters]);

  // Trigger a new search whenever the URL parameters change.
  useEffect(() => {
    fetchUnifiedSearch(searchParamValue);
  }, [searchParamValue, filters]);

  // ---- QUICK FILTERS ----
  // Define quick filters. For "All Recipes", an empty filters object indicates no filters.
  const quickFilters = [
    { label: "All recipes", emoji: "📖", filters: {} },
    { label: "Vegetarian", emoji: "🌱", filters: { diet: ["vegetarian", "vegan"] } },
    { label: "Breakfast", emoji: "🍳", filters: { mealType: ["breakfast"] } },
    { label: "Lunch", emoji: "🥪", filters: { mealType: ["lunch"] } },
    { label: "Dinner", emoji: "🍽", filters: { mealType: ["dinner"] } },
    { label: "Soup", emoji: "🥣", filters: { mealType: ["soup"] } },
    { label: "Salad", emoji: "🥗", filters: { mealType: ["salad"] } },
    { label: "Drink", emoji: "🍹", filters: { mealType: ["drink"] } },
    { label: "Dessert", emoji: "🍰", filters: { mealType: ["dessert"] } },
    { label: "Sauce", emoji: "🥫", filters: { mealType: ["sauce/dressing", "condiment"] } },

  ];

  // Map filter keys from quickFilters to our URL filters.
  const filterKeyMap = {
    diet: "diets",
    mealType: "mealTypes",
    cuisine: "cuisines",
    cookingMethod: "cookingMethods",
  };

  // Check if a quick filter is active by comparing its filters to current URL filters.
  const isQuickFilterActive = (quickFilter) => {
    const keys = Object.keys(quickFilter.filters);
    // For "All Recipes", active if no filters are set.
    if (keys.length === 0) {
      return (
        filters.mealTypes.length === 0 &&
        filters.diets.length === 0 &&
        filters.cuisines.length === 0 &&
        filters.cookingMethods.length === 0
      );
    }
    return keys.every((key) => {
      const current = filters[filterKeyMap[key]] || [];
      const desired = quickFilter.filters[key] || [];
      if (current.length !== desired.length) return false;
      return [...current].sort().join(",") === [...desired].sort().join(",");
    });
  };

  // Handle quick filter click: clear all existing filters and apply the quick filter.
  const handleQuickFilter = (quickFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      // Clear previous filters.
      params.delete("mealType");
      params.delete("diet");
      params.delete("cuisine");
      params.delete("cookingMethod");
      // Apply new filters from quickFilter.
      Object.keys(quickFilter.filters).forEach((key) => {
        params.set(key, quickFilter.filters[key].join(","));
      });
      return params;
    });
  };

  return (
    <div className="relative px-2 sm:px-6 py-8 max-w-7xl mx-auto">
      <h1 className="text-xl font-semibold mb-6">Discover recipes</h1>

      {/* Quick Filters Row */}
      <div className="mb-6">
        <div className="flex overflow-x-scroll space-x-4">
          {quickFilters.map((qf) => {
            const active = isQuickFilterActive(qf);
            return (
              <button
                key={qf.label}
                onClick={() => handleQuickFilter(qf)}
                className={`flex flex-col items-center justify-center text-sm font-medium hover:text-green-600 ${active ? "text-green-600 font-semibold" : "text-gray-600"
                  }`}
              >
                <span className="text-lg">{qf.emoji}</span>
                <span>{qf.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Responsive container for the search bar & filter button */}
      <div className="sm:flex sm:items-center sm:space-x-4 mb-8">
        {/* On small screens, search bar takes the full width. */}
        <div className="w-full mb-4 sm:mb-0">
          <SearchBar placeholder="Search recipes by name, ingredient, or link..." />
        </div>

        {/* Filter button */}
        <button
          onClick={() => setIsFilterOpen(true)}
          className={`
            bg-white hover:bg-gray-200 border border-gray-300 rounded-full py-3 px-8 shadow-sm font-semibold flex items-center
            sm:static sm:transform-none sm:translate-x-0 sm:bottom-auto sm:left-auto sm:z-auto sm:px-5
            fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 
          `}
        >
          {selectedCount > 0 ? (
            <span className="mr-2 inline-flex items-center justify-center w-5 h-5 bg-gray-500 text-white rounded-full text-xs leading-none">
              {selectedCount}
            </span>
          ) : (
            <FunnelIcon className="w-5 h-5 mr-2 text-gray-700" />
          )}
          Filter
        </button>
      </div>


      {/* No Results Message */}
      {(!loading &&
        (searchQuery.trim() || selectedCount > 0) &&
        recipes.length === 0) && (
          <p className="text-gray-500 text-sm mt-4">
            No matches found. Try refining your search with fewer filters or a more detailed search query.
          </p>
        )}

      {/* Main Content: Loader or Recipe Grid */}
      {loading ? (
        <RecipeGridLoader />
      ) : (
        <RecipeGrid recipes={recipes} setRecipes={setRecipes} showFavorite={true} />
      )}

      {/* Overlay & Filter Drawer with Transition */}
      <div
        className={`
          fixed inset-0 z-[1050] transition-opacity duration-700
          ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => setIsFilterOpen(false)}
          aria-hidden="true"
        ></div>
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </div>
    </div>
  );
}
